import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Trophy, 
  Target, 
  GraduationCap, 
  Gift, 
  Calendar, 
  FileText,
  Flame,
  Star,
  Crown,
  Award,
  Coins,
  TrendingUp,
  Zap,
  Loader2,
  Download
} from 'lucide-react';
import { PERMANENT_ACHIEVEMENTS, CYCLE_MILESTONES } from './achievements/achievementDefinitions';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import GameRulesEditableRow from './GameRulesEditableRow';
import GameRulesEditableCard from './GameRulesEditableCard';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

interface GameRulesEmbeddedProps {
  isAdmin?: boolean;
}

const GameRulesEmbedded: React.FC<GameRulesEmbeddedProps> = ({ isAdmin = false }) => {
  const { config, loading, updateConfigValue } = useScoringConfig();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Filter permanent achievements by category
  const permanentSales = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'ventas');
  const permanentRedemptions = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'canjes');
  const permanentSavings = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'ahorros');
  const permanentTraining = PERMANENT_ACHIEVEMENTS.filter(a => a.category === 'entrenamiento');

  // Filter cycle milestones by category
  const cycleSales = CYCLE_MILESTONES.filter(a => a.category === 'ventas');
  const cycleRedemptions = CYCLE_MILESTONES.filter(a => a.category === 'canjes');
  const cycleRanking = CYCLE_MILESTONES.filter(a => a.category === 'ranking');
  const cycleSavings = CYCLE_MILESTONES.filter(a => a.category === 'ahorros');
  const cycleSpecial = CYCLE_MILESTONES.filter(a => a.category === 'especial');

  const formatCurrency = (value: number) => `₡${(value / 1000).toFixed(0)}k`;

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return;

    setIsGeneratingPdf(true);
    toast.info('Generando PDF...');

    try {
      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#09090b',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      
      // Calculate pages needed
      const scaledHeight = imgHeight * ratio;
      const pageHeight = pdfHeight - 20; // margins
      let heightLeft = scaledHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - scaledHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
        heightLeft -= pageHeight;
      }

      const fileName = `reglas-puntuacion-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Admin Download Button */}
      {isAdmin && (
        <div className="flex justify-end">
          <Button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            variant="outline"
            size="sm"
            className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-white"
          >
            {isGeneratingPdf ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Descargar PDF
          </Button>
        </div>
      )}

      <div ref={contentRef} className="space-y-4 bg-zinc-950/95 rounded-xl p-4 md:p-6 border border-zinc-800/80">
        {/* Header */}
        <div className="text-center space-y-2 pb-4 border-b border-zinc-700/50">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 p-3 rounded-xl">
              <FileText className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-white">
            Reglas del Juego
          </h2>
          <div className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="border-blue-500/50 text-blue-300">
              {new Date().getFullYear()}
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="border-yellow-500/50 text-yellow-300">
                Modo Edición
              </Badge>
            )}
        </div>
      </div>

      {/* Section 1: Scoring System */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-blue-300 text-base">
            <Target className="h-5 w-5" />
            1. Sistema de Puntuación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-zinc-950 p-3 rounded-lg border border-blue-500/20">
            <p className="text-blue-300 font-mono text-center text-sm leading-relaxed">
              Score = Membresías + Canjes + Pasivos + Ahorros + Hitos + Logros
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left py-2 text-blue-400">Categoría</th>
                  <th className="text-left py-2 text-blue-400">Descripción</th>
                  <th className="text-right py-2 text-blue-400">Puntos</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                <GameRulesEditableRow
                  label="Bronce"
                  description="Por membresía Bronce"
                  value={config.points_bronce}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('points_bronce', v)}
                  labelClassName="text-amber-600"
                  valueClassName="text-green-400"
                />
                <GameRulesEditableRow
                  label="Plata"
                  description="Por membresía Plata"
                  value={config.points_plata}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('points_plata', v)}
                  labelClassName="text-slate-400"
                  valueClassName="text-green-400"
                />
                <GameRulesEditableRow
                  label="Oro"
                  description="Por membresía Oro"
                  value={config.points_oro}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('points_oro', v)}
                  labelClassName="text-yellow-400"
                  valueClassName="text-green-400"
                />
                <GameRulesEditableRow
                  label="Canje"
                  description="Por procesar un canje"
                  value={config.points_canje}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('points_canje', v)}
                  labelClassName="text-blue-400"
                  valueClassName="text-green-400"
                />
                <GameRulesEditableRow
                  label="Pasivo"
                  description="Cuando otro canjea tu venta"
                  value={config.points_pasivo}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('points_pasivo', v)}
                  labelClassName="text-purple-400"
                  valueClassName="text-green-400"
                />
                <tr className="border-b border-zinc-800">
                  <td className="py-2 font-medium text-emerald-400">Ahorros</td>
                  <td className="py-2 text-zinc-400">
                    Por cada ₡{(config.ahorro_threshold / 1000).toFixed(0)}k
                    {isAdmin && (
                      <span className="text-zinc-500 ml-1">(editable)</span>
                    )}
                  </td>
                  <td className="text-right">
                    <GameRulesEditableRow
                      label=""
                      value={config.points_ahorro_base}
                      isAdmin={isAdmin}
                      onSave={(v) => updateConfigValue('points_ahorro_base', v)}
                      valueClassName="text-green-400"
                    />
                  </td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-2 font-medium text-orange-400">Hitos</td>
                  <td className="py-2 text-zinc-400">Metas mensuales</td>
                  <td className="text-right text-purple-400 font-semibold">15-120</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-cyan-400">Logros</td>
                  <td className="py-2 text-zinc-400">Badges permanentes</td>
                  <td className="text-right text-purple-400 font-semibold">8-20</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Volume Bonuses */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-emerald-300 text-base">
            <Coins className="h-5 w-5" />
            2. Bonos por Volumen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-zinc-300 text-xs">
            Al vender <span className="text-white font-semibold">10 membresías del mismo tipo</span> en un mes:
          </p>
          
          <div className="grid grid-cols-3 gap-2">
            <GameRulesEditableCard
              emoji="🥉"
              title="10 Bronce"
              value={config.bonus_bronce_10}
              isAdmin={isAdmin}
              onSave={(v) => updateConfigValue('bonus_bronce_10', v)}
              borderColorClass="border-amber-600/30"
              titleColorClass="text-amber-600"
            />
            <GameRulesEditableCard
              emoji="🥈"
              title="10 Plata"
              value={config.bonus_plata_10}
              isAdmin={isAdmin}
              onSave={(v) => updateConfigValue('bonus_plata_10', v)}
              borderColorClass="border-slate-400/30"
              titleColorClass="text-slate-400"
            />
            <GameRulesEditableCard
              emoji="🥇"
              title="10 Oro"
              value={config.bonus_oro_10}
              isAdmin={isAdmin}
              onSave={(v) => updateConfigValue('bonus_oro_10', v)}
              borderColorClass="border-yellow-500/30"
              titleColorClass="text-yellow-400"
            />
          </div>
          
          <div className="bg-zinc-950 p-2 rounded-lg border border-emerald-500/20">
            <p className="text-zinc-400 text-xs text-center">
              <Zap className="h-3 w-3 inline text-yellow-400 mr-1" />
              Estos bonos se acumulan y son solo económicos (no dan puntos)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Permanent Achievements */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-purple-300 text-base">
            <Award className="h-5 w-5" />
            3. Logros Permanentes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-300 text-xs">
            Nunca se reinician. Sus puntos suman permanentemente.
          </p>
          
          {/* Sales Permanent */}
          {permanentSales.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Flame className="h-3 w-3 text-orange-400" />
                Ventas Acumuladas
              </h4>
              <div className="grid gap-1">
                {permanentSales.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Redemptions Permanent */}
          {permanentRedemptions.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Gift className="h-3 w-3 text-pink-400" />
                Canjes Acumulados
              </h4>
              <div className="grid gap-1">
                {permanentRedemptions.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Savings Permanent */}
          {permanentSavings.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <span className="text-base">💰</span>
                Ahorros Generados
              </h4>
              <div className="grid gap-1">
                {permanentSavings.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Training Permanent */}
          {permanentTraining.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <GraduationCap className="h-3 w-3 text-blue-400" />
                Entrenamiento
              </h4>
              <div className="grid gap-1">
                {permanentTraining.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Cycle Milestones */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-orange-300 text-base">
            <TrendingUp className="h-5 w-5" />
            4. Hitos del Ciclo (Mensuales)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-300 text-xs">
            Se <span className="text-white font-semibold">reinician cada mes</span>. ¡Compite para ganarlos nuevamente!
          </p>
          
          {/* Sales Cycle */}
          {cycleSales.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Flame className="h-3 w-3 text-orange-400" />
                Metas de Ventas
              </h4>
              <div className="grid gap-1">
                {cycleSales.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Redemptions Cycle */}
          {cycleRedemptions.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Gift className="h-3 w-3 text-pink-400" />
                Metas de Canjes
              </h4>
              <div className="grid gap-1">
                {cycleRedemptions.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Savings Cycle */}
          {cycleSavings.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <span className="text-base">💎</span>
                Metas de Ahorros
              </h4>
              <div className="grid gap-1">
                {cycleSavings.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ranking Cycle */}
          {cycleRanking.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Trophy className="h-3 w-3 text-yellow-400" />
                Posiciones del Ranking
              </h4>
              <div className="grid gap-1">
                {cycleRanking.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Cycle */}
          {cycleSpecial.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                <Star className="h-3 w-3 text-cyan-400" />
                Logros Especiales
              </h4>
              <div className="grid gap-1">
                {cycleSpecial.map((achievement) => (
                  <div key={achievement.type} className="flex items-center justify-between bg-zinc-950 p-2 rounded border border-zinc-800 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{achievement.badge}</span>
                      <span className="text-white">{achievement.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">+{achievement.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 5: Training */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-green-300 text-base">
            <GraduationCap className="h-5 w-5" />
            5. Entrenamiento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-950 p-3 rounded-lg border border-green-500/20">
              <h4 className="text-white font-semibold mb-2 text-sm">Estructura</h4>
              <ul className="space-y-1 text-zinc-300 text-xs">
                <li>✓ 6 módulos de formación</li>
                <li>✓ Quiz final por módulo</li>
                <li>✓ 80% para aprobar</li>
              </ul>
            </div>
            <div className="bg-zinc-950 p-3 rounded-lg border border-yellow-500/20">
              <h4 className="text-white font-semibold mb-2 text-sm">Recompensa</h4>
              <ul className="space-y-1 text-zinc-300 text-xs">
                <li>🎓 Badge "Experto VillaTech"</li>
                <li className="text-yellow-400 font-bold">+15 puntos permanentes</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 6: Monthly Prizes */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-yellow-300 text-base">
            <Gift className="h-5 w-5" />
            6. Premios Mensuales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-900/40 p-3 rounded-lg border border-yellow-500/40 text-center relative">
              <span className="text-2xl">🥇</span>
              <h4 className="text-yellow-400 font-bold text-xs mt-1">1er Lugar</h4>
              {isAdmin ? (
                <GameRulesEditableCard
                  emoji=""
                  title=""
                  value={config.premio_1er}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('premio_1er', v)}
                  borderColorClass="border-transparent"
                  titleColorClass="text-transparent"
                />
              ) : (
                <p className="text-lg font-bold text-white">{formatCurrency(config.premio_1er)}</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-slate-400/20 to-slate-600/40 p-3 rounded-lg border border-slate-400/40 text-center relative">
              <span className="text-2xl">🥈</span>
              <h4 className="text-slate-300 font-bold text-xs mt-1">2do Lugar</h4>
              {isAdmin ? (
                <GameRulesEditableCard
                  emoji=""
                  title=""
                  value={config.premio_2do}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('premio_2do', v)}
                  borderColorClass="border-transparent"
                  titleColorClass="text-transparent"
                />
              ) : (
                <p className="text-lg font-bold text-white">{formatCurrency(config.premio_2do)}</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-amber-700/20 to-amber-900/40 p-3 rounded-lg border border-amber-600/40 text-center relative">
              <span className="text-2xl">🥉</span>
              <h4 className="text-amber-500 font-bold text-xs mt-1">3er Lugar</h4>
              {isAdmin ? (
                <GameRulesEditableCard
                  emoji=""
                  title=""
                  value={config.premio_3er}
                  isAdmin={isAdmin}
                  onSave={(v) => updateConfigValue('premio_3er', v)}
                  borderColorClass="border-transparent"
                  titleColorClass="text-transparent"
                />
              ) : (
                <p className="text-lg font-bold text-white">{formatCurrency(config.premio_3er)}</p>
              )}
            </div>
          </div>
          <p className="text-zinc-500 text-xs text-center">
            * Valores editables por el administrador.
          </p>
        </CardContent>
      </Card>

      {/* Section 7: Evaluation Periods */}
      <Card className="bg-zinc-900/80 border-zinc-700/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-cyan-300 text-base">
            <Calendar className="h-5 w-5" />
            7. Períodos de Evaluación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-zinc-950 p-2 rounded-lg border border-zinc-700 text-center">
              <h4 className="text-white font-semibold text-xs">Semanal</h4>
              <p className="text-zinc-400 text-[10px]">Vista rápida</p>
            </div>
            <div className="bg-zinc-950 p-2 rounded-lg border border-yellow-500/30 text-center">
              <Crown className="h-4 w-4 text-yellow-400 mx-auto" />
              <h4 className="text-white font-semibold text-xs">Mensual</h4>
              <p className="text-zinc-400 text-[10px]">Principal</p>
            </div>
            <div className="bg-zinc-950 p-2 rounded-lg border border-zinc-700 text-center">
              <h4 className="text-white font-semibold text-xs">Histórico</h4>
              <p className="text-zinc-400 text-[10px]">Todo tiempo</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center pt-4 border-t border-zinc-700/50 space-y-1">
        <p className="text-zinc-500 text-xs">
          Última actualización: {new Date().toLocaleDateString('es-CR', { 
            year: 'numeric', 
            month: 'long'
          })}
        </p>
        {isAdmin && (
          <p className="text-yellow-500/70 text-xs">
            Los cambios se aplican inmediatamente al ranking
          </p>
        )}
      </div>
      </div>
    </div>
  );
};

export default GameRulesEmbedded;
